import type { ActivityEvent } from "@/pages/activity/api/activity";

/** The headline of a deal-type activity row, split around the prequalification
 *  reference it names. */
export interface DealTypeActivityPrequalLink {
  before: string;
  reference: string;
  after: string;
  href: string;
}

/**
 * Owner Recapitalization Loan (surface U4) — the server writes a deal-type
 * activity row (`DealTypeChanged`) for the recap auto-set, and on those rows the
 * metadata carries `prequalificationId` and `prequalificationReference`. Returns
 * the headline split around the reference so the row can link it to the staff
 * prequalification record, or null for any other row (or one whose headline
 * does not contain the reference).
 */
export function dealTypeActivityPrequalLink(event: ActivityEvent): DealTypeActivityPrequalLink | null {
  // Deal activity rows only (`module:deal:{id}`; the deal-flow audit rows are
  // `module:deal-audit:{id}`), and only the deal-type ones.
  if (!event.id.startsWith("module:deal:") || event.subline !== "DealTypeChanged") return null;
  const payload = asRecord(event.payload);
  const id = payload?.prequalificationId;
  const reference = payload?.prequalificationReference;
  if (typeof id !== "string" || id.trim() === "") return null;
  if (typeof reference !== "string" || reference.trim() === "") return null;
  const at = event.headline.indexOf(reference);
  if (at < 0) return null;
  return {
    before: event.headline.slice(0, at),
    reference,
    after: event.headline.slice(at + reference.length),
    href: `/deal-flow/prequalification/${encodeURIComponent(id.trim())}`,
  };
}

function asRecord(payload: unknown): Record<string, unknown> | null {
  let value = payload;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}
